import { useMutation, useQuery } from '@apollo/client'
import { AddCircle, Delete, Edit } from '@mui/icons-material'
import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Typography,
} from '@mui/material'
import { MonthCalendar, YearCalendar } from '@mui/x-date-pickers'
import {
    GetCompaniesByNameQuery,
    Industries,
    Sectors,
} from '__generated__/graphql'
import ToggleEditAutocomplete from 'candidate/components/ToggleEditFormFields/ToggleEditAutocomplete'
import ToggleEditCompaniesAutocomplete from 'candidate/components/ToggleEditFormFields/ToggleEditCompaniesAutocomplete'
import ToggleEditDatePicker from 'candidate/components/ToggleEditFormFields/ToggleEditDatePicker'
import ToggleEditTextField from 'candidate/components/ToggleEditFormFields/ToggleEditTextField'
import {
    EditButtonContainer,
    StyledForm,
    SubtitleContainer,
    TitleContainer,
} from 'candidate/components/styles'
import {
    GetCandidateWorkExperienceOptions,
    GetCompaniesById,
    UpdateCandidateWorkExperienceMutation,
} from 'candidate/graphql/generatedProfile.gql'
import { toMonthDate } from 'shared/utils/dateFormatter'
import isEqual from 'lodash.isequal'
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { isValidPhoneNumber } from 'react-phone-number-input'
import useToast from 'shared/components/Toast/useToast'
import { DropdownOption } from 'shared/models/dropdownOption'
import { getPxAsRem } from 'shared/utils'
import * as Yup from 'yup'
import { FormComponentProps, PROFILE_SECTIONS } from './types'

export interface WorkExperienceFormValues {
    id?: string
    isCurrent?: boolean
    company:
        | DropdownOption<
              GetCompaniesByNameQuery['companiesByName']['data'][number]
          >
        | undefined
    title: DropdownOption | undefined
    companyOffice:
        | {
              id?: string
              officeLocation: DropdownOption | undefined
          }
        | undefined
    industries: DropdownOption<Industries>[]
    sectors: DropdownOption<Sectors>[]
    startDate: string
    endDate: string
    workEmail: string
    workPhone: string
}

const WorkExperience: FC<FormComponentProps> = ({
    data,
    loading,
    refetch,
    edit,
    setEdit,
    closeEditWithoutModal,
    setUnsavedChangesExist,
}) => {
    const toast = useToast()
    const id = data.candidate?.user?.id

    const workExperienceOptions = useQuery(GetCandidateWorkExperienceOptions)
    const candidateWorkExperiences = useMemo(
        () =>
            data.candidate?.workExperience?.filter(
                (cwe) => cwe.id !== data.candidate?.mostRecentInternship?.id
            ) ?? [],
        [data.candidate?.workExperience, data.candidate?.mostRecentInternship]
    )

    const convertDataToFormValues = (candidateWorkExperiences) =>
        candidateWorkExperiences.map((experience) => ({
            id: experience.id ?? '',
            isCurrent: experience.isCurrent,
            company: {
                id: experience.company?.id ?? '',
                value: experience.company?.value ?? '',
            },
            title: {
                id: experience.title?.id ?? '',
                value: experience.title?.value ?? '',
            },
            companyOffice: experience.companyOffice
                ? {
                      id: experience.companyOffice?.id ?? '',
                      officeLocation:
                          experience.companyOffice?.officeLocation ?? undefined,
                  }
                : undefined,
            industries:
                experience.industries?.map(
                    (candidateIndustry) => candidateIndustry.industry
                ) ?? [],
            sectors:
                experience.sectors?.map(
                    (candidateSector) => candidateSector.sector
                ) ?? [],
            startDate: experience.startDate ?? '',
            endDate: experience.endDate ?? '',
            workEmail: experience.workEmail ?? '',
            workPhone: experience.workPhone ?? '',
        }))

    const initialState: WorkExperienceFormValues[] = useMemo(
        () => convertDataToFormValues(candidateWorkExperiences) ?? [],
        [candidateWorkExperiences]
    )
    const [formValues, setFormValues] =
        useState<WorkExperienceFormValues[]>(initialState)

    const emptyErrorValues = useMemo(
        () =>
            candidateWorkExperiences?.map(() => ({
                isCurrent: '',
                company: '',
                title: '',
                companyOffice: '',
                industries: '',
                sectors: '',
                workEmail: '',
                workPhone: '',
                startDate: '',
                endDate: '',
            })) ?? [],
        [candidateWorkExperiences]
    )

    const [errorValues, setErrorValues] = useState(emptyErrorValues)
    const [mutate] = useMutation(UpdateCandidateWorkExperienceMutation)

    const handleChange = (nextValue, index: number, field: string) => {
        setFormValues((prev) => {
            const next = [...prev]
            const newObj = { ...next[index] }
            newObj[field] = nextValue
            next[index] = newObj
            return next
        })
    }

    const handleErrorChange = (
        nextValue: string,
        index: number,
        field: string
    ) => {
        setErrorValues((prev) => {
            const next = [...prev]
            const newObj = { ...next[index] }
            newObj[field] = nextValue
            next[index] = newObj
            return next
        })
    }

    const handleEditClick = () => {
        if (edit) {
            setEdit(null)
        } else {
            setEdit(PROFILE_SECTIONS.WORK_EXPERIENCE)
        }
    }

    const resetForm = useCallback(() => {
        setFormValues(initialState)
        setErrorValues(emptyErrorValues)
    }, [setFormValues, initialState, emptyErrorValues])

    const handleRemove = (index) => {
        if (!edit) setEdit(PROFILE_SECTIONS.WORK_EXPERIENCE)
        setFormValues((prev) => {
            const next = [...prev]
            next.splice(index, 1)
            return next
        })
        setErrorValues((prev) => {
            const next = [...prev]
            next.splice(index, 1)
            return next
        })
    }

    const handleAddWorkExperience = () => {
        if (!edit) setEdit(PROFILE_SECTIONS.WORK_EXPERIENCE)
        setFormValues((prev) => [
            ...prev,
            {
                id: '',
                isCurrent: false,
                company: undefined,
                title: undefined,
                companyOffice: undefined,
                industries: [],
                sectors: [],
                workEmail: '',
                workPhone: '',
                startDate: '',
                endDate: '',
            },
        ])
        setErrorValues((prev) => [
            ...prev,
            {
                isCurrent: '',
                company: '',
                title: '',
                companyOffice: '',
                industries: '',
                sectors: '',
                workEmail: '',
                workPhone: '',
                startDate: '',
                endDate: '',
            },
        ])
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // multiple submission type scenarios to check for
        const currentWorkExperiences = formValues
            .map((i) => (Boolean(i.endDate) ? i : { ...i, endDate: '' }))
            .filter((workExperience) => workExperience.isCurrent)

        if (currentWorkExperiences.length > 1) {
            toast({
                title: 'Error',
                message: 'You can only have one current work experience',
                severity: 'error',
            })
            return
        }

        try {
            const response = await mutate({
                variables: {
                    userId: id,
                    workExperience: formValues.map((formValue) => ({
                        id: formValue?.id || undefined,
                        isCurrent: formValue.isCurrent,
                        companyId: formValue.company?.id,
                        titleId: formValue.title?.id,
                        companyOfficeId: formValue.companyOffice?.id,
                        industryIds: formValue.industries?.map((i) => i.id),
                        sectorIds: formValue.sectors?.map((s) => s.id),
                        workEmail: formValue.workEmail,
                        workPhone: formValue.workPhone,
                        startDate: formValue.startDate,
                        endDate: formValue.endDate || null,
                    })),
                },
            })
            if (response.data?.updateCandidateWorkExperiences?.success) {
                toast({
                    title: 'Success!',
                    message: 'Work experience has been updated',
                    severity: 'success',
                })
                refetch()
                closeEditWithoutModal()
            } else {
                toast({
                    title: 'Error',
                    message:
                        response?.data?.updateCandidateWorkExperiences
                            .message || 'Failed to update work experience',
                    severity: 'error',
                })
            }
        } catch (e) {
            console.log(e)
            toast({
                title: 'Error',
                message: 'Failed to update work experience',
                severity: 'error',
            })
        }
    }

    const unsavedChangesExist = useMemo(() => {
        // if lengths are different, we have obviously added a new one we need to save
        if (formValues.length !== candidateWorkExperiences?.length) return true
        // if we have any values without ids it means they are new / not edits
        if (formValues.filter((v) => !v.id).length > 0) return true
        // check if objects are equal after mapping the data values to form values
        const cwesToCompare = convertDataToFormValues(candidateWorkExperiences)
        if (!isEqual(formValues, cwesToCompare)) return true

        return false
    }, [formValues, candidateWorkExperiences])

    const isDisabled = useMemo(() => {
        if (!unsavedChangesExist) return true
        const hasEmptyValues = formValues.reduce((acc, formValue) => {
            if (acc) {
                return acc
            } else {
                const value: Partial<WorkExperienceFormValues> = {
                    ...formValue,
                }
                // Remove the following keys from the object before checking as they are allowed to be empty
                delete value.id
                if (value.isCurrent) {
                    delete value.endDate
                } else if (value.endDate) {
                    delete value.isCurrent
                }
                delete value.companyOffice
                return Object.values(value).some(
                    (value) =>
                        value === '' || isEqual(value, { id: '', value: '' })
                )
            }
        }, false)
        if (hasEmptyValues) return true
        const hasErrors = errorValues?.reduce((acc, errorValue) => {
            if (acc) {
                return acc
            } else {
                return Object.values(errorValue).some((value) => value !== '')
            }
        }, false)
        return hasErrors
    }, [formValues, errorValues, unsavedChangesExist])

    const { data: officeLocationOptions } = useQuery(GetCompaniesById, {
        variables: {
            ids: formValues
                .map((fv) => fv.company?.id)
                .filter((val) => val !== undefined),
        },
    })

    // pass the unsavedChanges value for this form to the parent to control the unsaved changes modal behavior
    useEffect(
        () => setUnsavedChangesExist(unsavedChangesExist),
        [unsavedChangesExist, setUnsavedChangesExist]
    )

    useEffect(() => {
        if (!edit) resetForm()
    }, [edit, resetForm])

    return (
        <>
            <TitleContainer>
                <Typography variant="h6" component="h2">
                    Work Experience
                </Typography>
                <IconButton onClick={handleEditClick} disabled={loading}>
                    <Edit fontSize="small" />
                </IconButton>
            </TitleContainer>
            <StyledForm onSubmit={handleSubmit}>
                <Grid container rowSpacing={3} columnSpacing={2}>
                    {formValues.map(
                        (
                            {
                                company,
                                title,
                                companyOffice,
                                industries,
                                sectors,
                                workEmail,
                                workPhone,
                                startDate,
                                endDate,
                                isCurrent,
                                id,
                            },
                            index
                        ) => (
                            <Fragment key={id || index}>
                                <SubtitleContainer item xs={12}>
                                    <Typography variant="subtitle2">
                                        Experience {index + 1}
                                    </Typography>
                                    {(index !== 0 ||
                                        formValues?.length > 1) && (
                                        <IconButton
                                            onClick={() => handleRemove(index)}
                                            aria-label="remove work experience"
                                        >
                                            <Delete />
                                        </IconButton>
                                    )}
                                </SubtitleContainer>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditCompaniesAutocomplete
                                        edit={edit}
                                        label="Company"
                                        labelId="workExperienceCompany"
                                        inCandidatePortal
                                        helperText={errorValues[index].company}
                                        error={!!errorValues[index].company}
                                        value={company}
                                        size="medium"
                                        onBlur={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => {
                                            if (!e.target.value) {
                                                handleErrorChange(
                                                    'Company is required',
                                                    index,
                                                    'company'
                                                )
                                            }
                                        }}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'company'
                                                )
                                            } else {
                                                handleErrorChange(
                                                    'Company is required',
                                                    index,
                                                    'company'
                                                )
                                            }
                                            handleChange(
                                                newValue,
                                                index,
                                                'company'
                                            )
                                            handleChange(
                                                null,
                                                index,
                                                'companyOffice'
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditAutocomplete
                                        edit={edit}
                                        label="Title"
                                        labelId="workExperienceTitle"
                                        helperText={errorValues[index].title}
                                        error={!!errorValues[index].title}
                                        options={
                                            workExperienceOptions.data
                                                ?.titles || []
                                        }
                                        value={title}
                                        onBlur={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => {
                                            if (!e.target.value) {
                                                handleErrorChange(
                                                    'Title is required',
                                                    index,
                                                    'title'
                                                )
                                            }
                                        }}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'title'
                                                )
                                            } else {
                                                handleErrorChange(
                                                    'Title is required',
                                                    index,
                                                    'title'
                                                )
                                            }
                                            handleChange(
                                                newValue,
                                                index,
                                                'title'
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditAutocomplete
                                        edit={edit}
                                        label="Office Location"
                                        labelId="workExperienceCompanyOffice"
                                        disabled={!company}
                                        helperText={
                                            errorValues[index].companyOffice
                                        }
                                        error={
                                            !!errorValues[index].companyOffice
                                        }
                                        options={
                                            officeLocationOptions?.companiesById
                                                ?.find(
                                                    (o) => o.id === company?.id
                                                )
                                                ?.companyOffices?.map(
                                                    (co) => co.officeLocation
                                                )
                                                .filter(
                                                    (v) =>
                                                        v !== undefined &&
                                                        v !== null
                                                ) || []
                                        }
                                        value={
                                            companyOffice?.officeLocation ||
                                            null
                                        }
                                        onBlur={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => {
                                            if (!e.target.value) {
                                                handleErrorChange(
                                                    'Office location is required',
                                                    index,
                                                    'companyOffice'
                                                )
                                            }
                                        }}
                                        onChange={(event, newValue) => {
                                            if (Array.isArray(newValue)) {
                                            } else {
                                                if (newValue) {
                                                    handleErrorChange(
                                                        '',
                                                        index,
                                                        'companyOffice'
                                                    )
                                                } else {
                                                    handleErrorChange(
                                                        'Office location is required',
                                                        index,
                                                        'companyOffice'
                                                    )
                                                }
                                                const companyOfficeOptionsForSelectedCompany =
                                                    officeLocationOptions?.companiesById
                                                        .filter(
                                                            (c) =>
                                                                c.id ===
                                                                company?.id
                                                        )
                                                        .flatMap(
                                                            (c) =>
                                                                c.companyOffices
                                                        )

                                                const newCompanyOffice =
                                                    companyOfficeOptionsForSelectedCompany?.find(
                                                        (co) =>
                                                            co?.officeLocation
                                                                ?.id ===
                                                            newValue?.id
                                                    )

                                                handleChange(
                                                    newCompanyOffice,
                                                    index,
                                                    'companyOffice'
                                                )
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditAutocomplete
                                        multiple
                                        edit={edit}
                                        label="Industry"
                                        labelId="workExperienceIndustry"
                                        helperText={
                                            errorValues[index].industries
                                        }
                                        error={!!errorValues[index].industries}
                                        options={
                                            workExperienceOptions.data
                                                ?.industries || []
                                        }
                                        onBlur={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => {
                                            if (industries.length === 0) {
                                                handleErrorChange(
                                                    'Industry is required',
                                                    index,
                                                    'industries'
                                                )
                                            }
                                        }}
                                        value={industries}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'industries'
                                                )
                                            } else {
                                                handleErrorChange(
                                                    'Industry is required',
                                                    index,
                                                    'industries'
                                                )
                                            }
                                            handleChange(
                                                newValue,
                                                index,
                                                'industries'
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditAutocomplete
                                        multiple
                                        edit={edit}
                                        label="Sector"
                                        labelId="workExperienceSector"
                                        helperText={errorValues[index].sectors}
                                        error={!!errorValues[index].sectors}
                                        options={
                                            workExperienceOptions.data
                                                ?.sectors || []
                                        }
                                        onBlur={(
                                            e: React.FocusEvent<HTMLInputElement>
                                        ) => {
                                            if (sectors.length === 0) {
                                                handleErrorChange(
                                                    'Sector is required',
                                                    index,
                                                    'sectors'
                                                )
                                            }
                                        }}
                                        value={sectors}
                                        onChange={(event, newValue) => {
                                            if (newValue) {
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'sectors'
                                                )
                                            } else {
                                                handleErrorChange(
                                                    'Sector is required',
                                                    index,
                                                    'sectors'
                                                )
                                            }
                                            handleChange(
                                                newValue,
                                                index,
                                                'sectors'
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditTextField
                                        edit={edit}
                                        label="Work Email"
                                        name="workEmail"
                                        value={workEmail}
                                        helperText={
                                            errorValues[index].workEmail
                                        }
                                        onBlur={(e) => {
                                            if (
                                                e.target.value &&
                                                !Yup.string()
                                                    .email()
                                                    .isValidSync(e.target.value)
                                            ) {
                                                handleErrorChange(
                                                    'Work Email is not valid',
                                                    index,
                                                    'workEmail'
                                                )
                                            } else {
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'workEmail'
                                                )
                                            }
                                        }}
                                        error={!!errorValues[index].workEmail}
                                        onChange={(e) => {
                                            handleErrorChange(
                                                '',
                                                index,
                                                'workEmail'
                                            )
                                            handleChange(
                                                e.target.value,
                                                index,
                                                'workEmail'
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditTextField
                                        mask="phone"
                                        edit={edit}
                                        label="Work Phone"
                                        name="workPhone"
                                        value={workPhone}
                                        helperText={
                                            errorValues[index].workPhone
                                        }
                                        onBlur={(e) => {
                                            if (
                                                e.target.value &&
                                                !isValidPhoneNumber(workPhone)
                                            ) {
                                                handleErrorChange(
                                                    'Work Phone is invalid',
                                                    index,
                                                    'workPhone'
                                                )
                                            }
                                        }}
                                        error={!!errorValues[index].workPhone}
                                        onChange={(newValue) => {
                                            handleErrorChange(
                                                '',
                                                index,
                                                'workPhone'
                                            )
                                            handleChange(
                                                newValue,
                                                index,
                                                'workPhone'
                                            )
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditDatePicker
                                        onBlur={(e) => {
                                            if (!e.target.value) {
                                                handleErrorChange(
                                                    'Start Date is required',
                                                    index,
                                                    'startDate'
                                                )
                                            }
                                        }}
                                        edit={edit}
                                        label="Start Date"
                                        value={toMonthDate(
                                            startDate,
                                            'dateobject'
                                        )}
                                        helperText={
                                            errorValues[index].startDate
                                        }
                                        error={!!errorValues[index].startDate}
                                        format="MM/yyyy"
                                        views={['month', 'year']}
                                        onChange={(date, context) => {
                                            // prevents saving the value before it's a valid date (happens when using arrow keys within the input before selection from empty state)
                                            if (context.validationError) {
                                                return
                                            }
                                            if (date) {
                                                handleErrorChange(
                                                    '',
                                                    index,
                                                    'startDate'
                                                )
                                                handleChange(
                                                    toMonthDate(
                                                        date,
                                                        'MM-yyyy'
                                                    ),
                                                    index,
                                                    'startDate'
                                                )
                                            } else {
                                                handleErrorChange(
                                                    'Start Date is required',
                                                    index,
                                                    'startDate'
                                                )
                                                handleChange(
                                                    null,
                                                    index,
                                                    'startDate'
                                                )
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ToggleEditDatePicker
                                        edit={edit}
                                        emptyLabel="Present"
                                        label="End Date"
                                        placeholder="Present"
                                        helperText={errorValues[index].endDate}
                                        error={!!errorValues[index].endDate}
                                        value={toMonthDate(
                                            endDate,
                                            'dateobject'
                                        )}
                                        views={['month', 'year']}
                                        format="MM/yyyy"
                                        onChange={(date, context) => {
                                            // prevents saving the value before it's a valid date (happens when using arrow keys within the input before selection from empty state)
                                            if (context.validationError) {
                                                return
                                            }
                                            if (date) {
                                                handleChange(
                                                    toMonthDate(
                                                        date,
                                                        'MM-yyyy'
                                                    ),
                                                    index,
                                                    'endDate'
                                                )
                                                handleChange(
                                                    false,
                                                    index,
                                                    'isCurrent'
                                                )
                                            } else {
                                                handleChange(
                                                    null,
                                                    index,
                                                    'endDate'
                                                )
                                            }
                                        }}
                                        viewRenderers={{
                                            month: (props) => (
                                                <>
                                                    <MonthCalendar
                                                        sx={{
                                                            paddingBottom:
                                                                getPxAsRem(48),
                                                        }}
                                                        onChange={(value) => {
                                                            const currentYear =
                                                                props.value?.getFullYear() ||
                                                                new Date().getFullYear()
                                                            const currentMonth =
                                                                value.getMonth()
                                                            const newDate =
                                                                new Date(
                                                                    currentYear,
                                                                    currentMonth,
                                                                    1
                                                                )
                                                            handleChange(
                                                                toMonthDate(
                                                                    newDate,
                                                                    'MM-yyyy'
                                                                ),
                                                                index,
                                                                'endDate'
                                                            )
                                                            handleChange(
                                                                false,
                                                                index,
                                                                'isCurrent'
                                                            )
                                                            props.onViewChange &&
                                                                props?.onViewChange(
                                                                    'year'
                                                                )
                                                        }}
                                                    />
                                                    <FormControlLabel
                                                        checked={isCurrent}
                                                        onChange={(
                                                            event,
                                                            checked
                                                        ) => {
                                                            if (checked) {
                                                                handleChange(
                                                                    undefined,
                                                                    index,
                                                                    'endDate'
                                                                )
                                                                handleChange(
                                                                    true,
                                                                    index,
                                                                    'isCurrent'
                                                                )
                                                                props.onChange(
                                                                    undefined
                                                                )
                                                                props.onClose()
                                                            } else {
                                                                handleChange(
                                                                    new Date().toISOString(),
                                                                    index,
                                                                    'endDate'
                                                                )
                                                                handleChange(
                                                                    false,
                                                                    index,
                                                                    'isCurrent'
                                                                )
                                                                props.onChange(
                                                                    new Date()
                                                                )
                                                                props.onClose()
                                                            }
                                                        }}
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            bottom: '0',
                                                            margin: getPxAsRem(
                                                                0,
                                                                0,
                                                                8,
                                                                12
                                                            ),
                                                        }}
                                                        control={<Checkbox />}
                                                        label="I am currently working in this role"
                                                    />
                                                </>
                                            ),
                                            year: (props) => (
                                                <>
                                                    <YearCalendar
                                                        sx={{
                                                            marginBottom:
                                                                getPxAsRem(48),
                                                        }}
                                                        onChange={(value) => {
                                                            const newMonth =
                                                                props.value?.getMonth() ||
                                                                0
                                                            const newYear =
                                                                value.getFullYear()
                                                            const newDate =
                                                                new Date(
                                                                    newYear,
                                                                    newMonth,
                                                                    1
                                                                )
                                                            handleChange(
                                                                toMonthDate(
                                                                    newDate,
                                                                    'MM-yyyy'
                                                                ),
                                                                index,
                                                                'endDate'
                                                            )
                                                            props.onClose()
                                                        }}
                                                    />
                                                    <FormControlLabel
                                                        checked={!endDate}
                                                        onChange={(
                                                            event,
                                                            checked
                                                        ) => {
                                                            if (checked) {
                                                                handleChange(
                                                                    undefined,
                                                                    index,
                                                                    'endDate'
                                                                )
                                                                props.onChange(
                                                                    undefined
                                                                )
                                                                props.onClose()
                                                            } else {
                                                                handleChange(
                                                                    new Date().toISOString(),
                                                                    index,
                                                                    'endDate'
                                                                )
                                                                props.onChange(
                                                                    new Date()
                                                                )
                                                                props.onClose()
                                                            }
                                                        }}
                                                        sx={{
                                                            position:
                                                                'absolute',
                                                            bottom: '0',
                                                            margin: getPxAsRem(
                                                                0,
                                                                0,
                                                                8,
                                                                12
                                                            ),
                                                        }}
                                                        control={<Checkbox />}
                                                        label="I am currently working in this role"
                                                    />
                                                </>
                                            ),
                                        }}
                                    />
                                    {index === formValues.length - 1 && (
                                        <EditButtonContainer item xs={12}>
                                            <Button
                                                onClick={
                                                    handleAddWorkExperience
                                                }
                                                variant="text"
                                                startIcon={<AddCircle />}
                                            >
                                                Add Experience
                                            </Button>
                                        </EditButtonContainer>
                                    )}
                                </Grid>
                            </Fragment>
                        )
                    )}
                    {!formValues.length && (
                        <EditButtonContainer item xs={12}>
                            <Button
                                onClick={handleAddWorkExperience}
                                variant="text"
                                startIcon={<AddCircle />}
                            >
                                Add Experience
                            </Button>
                        </EditButtonContainer>
                    )}

                    {edit && (
                        <EditButtonContainer item xs={12}>
                            <Button
                                variant="outlined"
                                type="reset"
                                onClick={() => {
                                    closeEditWithoutModal()
                                    resetForm()
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" disabled={isDisabled}>
                                Save and Update
                            </Button>
                        </EditButtonContainer>
                    )}
                </Grid>
            </StyledForm>
        </>
    )
}

export default WorkExperience
